import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  ArrowLeft,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Package,
  TrendingUp
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import approverMasterService from '../../services/approverMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ApproverMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalApprovers: 0,
    activeApprovers: 0,
    inactiveApprovers: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [approversResponse, statsResponse] = await Promise.all([
        approverMasterService.getAllApprovers(),
        approverMasterService.getApproverStats()
      ])
      
      if (approversResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (approversResponse.data || []).map(approver => ({
          ...approver,
          id: approver._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (approversResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No approver records found. Create your first approver!')
        }
      } else {
        toast.error(approversResponse.message || 'Failed to load approvers')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading approver master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view approvers')
      } else {
        toast.error('Failed to load approver master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'approverCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'approverName',
      header: 'Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ getValue }) => (
        <div className="sac-cell" style={{ textAlign: 'left' }}>
          <span className="sac-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'department',
      header: 'Department',
      cell: ({ getValue }) => (
        <div className="sac-cell" style={{ textAlign: 'center' }}>
          <span className="sac-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'approvalLevel',
      header: 'Approval Level',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span>{getValue() || 1}</span>
        </div>
      ),
    },
    {
      accessorKey: 'approvalScope',
      header: 'Scope',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span>{getValue() || 'All'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'approvalLimits.perTransactionLimit',
      header: 'Per Transaction Limit',
      cell: ({ row }) => {
        const limit = row.original.approvalLimits?.perTransactionLimit || 0
        const currency = row.original.approvalLimits?.currency || 'INR'
        return (
          <div className="active-cell" style={{ textAlign: 'right' }}>
            <span>{limit > 0 ? `${currency} ${limit.toLocaleString()}` : '-'}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/expenses/approver-master/form')
  }

  const handleEdit = (approverData) => {
    navigate('/expenses/approver-master/edit', { state: { approverData } })
  }

  const handleView = (approverData) => {
    navigate('/expenses/approver-master/view', { state: { approverData } })
  }

  const handleDelete = (approverData) => {
    setDeleteItem(approverData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting approver...')
      // Use _id instead of id
      const response = await approverMasterService.deleteApprover(deleteItem._id)
      if (response.success) {
        toast.success('Approver deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete approver')
      }
    } catch (error) {
      console.error('Error deleting approver:', error)
      if (error.response?.status === 404) {
        toast.error('Approver not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this approver')
      } else {
        toast.error('Failed to delete approver. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    // TODO: Implement export functionality
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    {
      label: 'Expenses',
      path: '/expenses',
      onClick: () => navigate('/expenses')
    },
    {
      label: 'Approver Master',
      path: '/expenses/approver-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Approver Master</h1>
          <p>Manage expense approval hierarchy and limits</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            Add Approver
          </button>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.totalApprovers}</span>
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalApprovers}</div>
          <div className="metric-title">Total Approvers</div>
          <div className="metric-subtitle">All approvers</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.activeApprovers}</span>
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeApprovers}</div>
          <div className="metric-title">Active Approvers</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.inactiveApprovers}</span>
            </div>
            <div className="metric-trend negative">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveApprovers}</div>
          <div className="metric-title">Inactive Approvers</div>
          <div className="metric-subtitle">Currently inactive</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExport={handleExport}
          noCard={true}
          className="sac-master-table"
          pageSize={10}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Approver"
        message={`Are you sure you want to delete "${deleteItem?.approverName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ApproverMaster

